import { useMutation, useQuery } from "@apollo/client";
import {
  USER_SETTINGS,
  UPDATE_MEETING_BOT_SETTINGS,
  UPDATE_PREFRENCES,
  UPDATE_LABEL_SETTINGS,
  GET_LABEL_SETTINGS,
  UPDATE_ARCHIVE_SETTINGS,
  GET_ARCHIVE_SETTING,
  GET_CALENDAR_LINK,
  UPDATE_CALENDAR_LINK,
} from "@repo-sapiens/graphql";
import {
  PageLayout,
  Separator,
  ServiceOptionsItem,
  Text,
  ToastMessage,
} from "../../../components";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { EActivityEvent } from "@repo-sapiens/data-access-models";
import { NotificationStyle } from "../styles";
import { InputItem, TextEditor } from "../components";
import { usePageViewed, useSubscriptionPlan } from "../../../hooks";
import { UserContext } from "../../../context";
import { GetSettingResponse } from "../../../types";

let debounce: NodeJS.Timeout | null = null;

export enum E_GmailLabelBehavior {
  MultiplayerDecide = "multiplayer-decide",
  ArchiveNow = "archive-immediately",
  NeverArchive = "never-archive",
  //ArchiveIfRead = 'archive-if-read',
  //ArchiveIn1HourIfRead = 'archive-in-1h-if-read',
  ArchiveAtEndOfDay = "archive-at-end-of-day",
}

export const DEFAULT_LABEL_BEHAVIOR = {
  "@username": E_GmailLabelBehavior.MultiplayerDecide,
  calendar: E_GmailLabelBehavior.MultiplayerDecide,
  events: E_GmailLabelBehavior.MultiplayerDecide,
  "financial/receipts": E_GmailLabelBehavior.MultiplayerDecide,
  travel: E_GmailLabelBehavior.MultiplayerDecide,
  newsletters: E_GmailLabelBehavior.MultiplayerDecide,
  notifications: E_GmailLabelBehavior.MultiplayerDecide,
  orders: E_GmailLabelBehavior.MultiplayerDecide,
  promotional: E_GmailLabelBehavior.MultiplayerDecide,
  "sales discounts": E_GmailLabelBehavior.MultiplayerDecide,
  sales: E_GmailLabelBehavior.MultiplayerDecide,
};

export enum E_EmailArchiveLevel {
  Aggressive = "aggressive",
  Default = "default",
  Custom = "custom",
  Off = "off",
}

const inboxSettingsOptions = [
  {
    label: "Archive immediately",
    value: E_GmailLabelBehavior.ArchiveNow,
  },
  //{
  //  label: "Archive if read",
  //  value: E_GmailLabelBehavior.ArchiveIfRead,
  //},
  //{
  //  label: "Archive in 1h if read",
  //  value: E_GmailLabelBehavior.ArchiveIn1HourIfRead,
  //},
  {
    label: "Archive at the end of the day",
    value: E_GmailLabelBehavior.ArchiveAtEndOfDay,
  },
  {
    label: "Let Multiplayer decide",
    value: E_GmailLabelBehavior.MultiplayerDecide,
  },
  {
    label: "Never archive",
    value: E_GmailLabelBehavior.NeverArchive,
  },
];

const meetingJoinOptions = [
  {
    label: "All meetings",
    value: "all",
  },
  {
    label: "External meetings only",
    value: "external",
  },
  {
    label: "Internal meetings only",
    value: "internal",
  },
  {
    label: "Internal 1:1 meetings only",
    value: "1:1",
  },
  {
    label: "Disable for all meetings",
    value: "disabled",
  },
];

const followupEmailOptions = [
  {
    label: "All meetings",
    value: "all",
  },
  {
    label: "External meetings only",
    value: "external",
  },
  {
    label: "Internal meetings only",
    value: "internal",
  },
  {
    label: "Internal 1:1 meetings only",
    value: "1:1",
  },
  {
    label: "Disable for all meetings",
    value: "disabled",
  },
];

const meetingNoteEmailOptions = [
  {
    label: "Just me",
    value: "me",
  },
  {
    label: "All attendees",
    value: "all",
  },
  {
    label: "External attendees only",
    value: "external",
  },
  {
    label: "Internal attendees only",
    value: "internal",
  },
  {
    label: "Disable for all meetings",
    value: "disabled",
  },
];

type MeetingBotSettings = GetSettingResponse["me"]["meetingBotSettings"];
type LabelSettings = {
  name: string;
  defaultBehavior: E_GmailLabelBehavior;
};
type LabelSettingsResponse = {
  inboxLabelSettings: LabelSettings[];
};

export const AssistantSetupSreen: React.FC = () => {
  const [allDisabled, setAllDisabled] = useState(false);
  const [signature, setSignature] = useState("");
  const [calendly, setCalendly] = useState("");

  const { state: userState } = useContext(UserContext);

  const {
    data: settingsData,
    refetch: refetchSettings,
    loading,
  } = useQuery<GetSettingResponse>(USER_SETTINGS, {
    fetchPolicy: "network-only",
  });

  const {
    data: labelSettingsData,
    refetch: refetchLabelSettings,
    loading: labelSettingsLoading,
  } = useQuery<LabelSettingsResponse>(GET_LABEL_SETTINGS, {
    fetchPolicy: "network-only",
  });

  const { data: calendarLinkData, refetch: refetchCalendarSettings } =
    useQuery<{ calendarLink?: string }>(GET_CALENDAR_LINK, {
      fetchPolicy: "network-only",
    });

  const { data: archiveData, refetch: refetchArchive } =
    useQuery(GET_ARCHIVE_SETTING);

  const { isFree, vipUser } = useSubscriptionPlan();

  const [settings, setSettings] = useState<MeetingBotSettings>(
    {} as MeetingBotSettings,
  );
  const [inboxManagement, setInboxManagement] = useState<LabelSettings[]>([]);
  const [inboxBehavior, setInboxBehavior] = useState<string>("");

  useEffect(() => {
    if (archiveData?.emailArchiveLevel) {
      setInboxBehavior(archiveData.emailArchiveLevel);
    }
  }, [archiveData?.emailArchiveLevel]);

  useEffect(() => {
    if (calendarLinkData?.calendarLink) {
      setCalendly(calendarLinkData.calendarLink);
    }
  }, [calendarLinkData?.calendarLink]);

  useEffect(() => {
    if (labelSettingsData?.inboxLabelSettings && !labelSettingsLoading) {
      setInboxManagement(
        labelSettingsData.inboxLabelSettings.map(
          (setting) =>
            ({
              name: setting.name,
              defaultBehavior: setting.defaultBehavior,
            }) as LabelSettings,
        ),
      );
    }
  }, [labelSettingsData?.inboxLabelSettings, labelSettingsLoading]);

  useEffect(() => {
    if (settingsData?.me?.meetingBotSettings && !loading) {
      setSettings({
        followUpEmail: settingsData.me.meetingBotSettings.followUpEmail,
        meetingNoteEmail: settingsData.me.meetingBotSettings.meetingNoteEmail,
        joinMeetings: settingsData.me.meetingBotSettings.joinMeetings || "all",
      });
    }
    if (
      settingsData?.me?.preferences &&
      settingsData?.me?.preferences.emailSignature &&
      !loading
    ) {
      setSignature(settingsData.me.preferences.emailSignature);
    }
  }, [
    settingsData?.me?.meetingBotSettings,
    settingsData?.me?.preferences,
    loading,
  ]);

  const [showToast, setShowToast] = useState(false);

  const [update] = useMutation(UPDATE_MEETING_BOT_SETTINGS);
  const [updateAchiveLevel] = useMutation(UPDATE_ARCHIVE_SETTINGS);
  const [updateDraftSettings] = useMutation(UPDATE_PREFRENCES);
  const [updateLabelSettings] = useMutation(UPDATE_LABEL_SETTINGS);
  const [updateCalendarLink] = useMutation(UPDATE_CALENDAR_LINK);

  usePageViewed(EActivityEvent.AssistantSetupPageViewed);

  const inboxManagementOptions = useMemo(
    () => [
      {
        label: "Off",
        description: "No emails are automatically archived.",
        value: E_EmailArchiveLevel.Off,
      },
      {
        label: "Default",
        description: `Emails not labeled @${userState?.firstName || "me"} are auto-archived. Your inbox only shows what likely needs your attention.`,
        value: E_EmailArchiveLevel.Default,
      },
      {
        label: "Aggressive",
        description:
          "Keeps your inbox near zero by auto-archiving most emails, except those deemed urgent or important. You’ll primarily manage email from labeled folders.",
        value: E_EmailArchiveLevel.Aggressive,
      },
      {
        label: "Custom",
        description:
          "Define granular archiving rules for specific labels (see below).",
        value: E_EmailArchiveLevel.Custom,
      },
    ],
    [userState?.firstName],
  );

  const submitChanges = async (
    key: keyof MeetingBotSettings,
    value: boolean | string,
  ) => {
    update({
      variables: {
        data: {
          ...settings,
          [key]: value,
        },
      },
    }).then(() => {
      setShowToast(true);
      refetchSettings();
    });
  };

  const draftSettingSubmit = async (value: string) => {
    updateDraftSettings({
      variables: {
        data: {
          emailSignature: value,
        },
      },
    }).then(() => {
      setShowToast(true);
      refetchSettings();
    });
  };

  const handleSelectBehavior = (value: E_EmailArchiveLevel) => {
    setInboxBehavior(value);
    updateAchiveLevel({
      variables: {
        level: value,
      },
    }).then(() => {
      setShowToast(true);
      refetchArchive();
    });
  };

  const handleSelect = (
    key: keyof MeetingBotSettings,
    value: boolean | string,
  ) => {
    if (key === "joinMeetings" && value === "disabled") {
      setAllDisabled(!value);
    }
    setSettings({ ...settings, [key]: value });
    submitChanges(key, value);
  };

  const handleInboxManagementSelect = (key: string, value: string) => {
    const newState = JSON.parse(
      JSON.stringify(inboxManagement),
    ) as LabelSettings[];
    const index = newState.findIndex((s) => s.name === key);
    if (index !== -1) {
      newState[index].defaultBehavior = value as E_GmailLabelBehavior;
    } else {
      newState.push({
        name: key,
        defaultBehavior: value as E_GmailLabelBehavior,
      });
    }
    setInboxManagement(newState);
    setShowToast(true);
    updateLabelSettings({
      variables: {
        settings: {
          data: newState,
        },
      },
    }).then(() => {
      refetchLabelSettings();
    });
  };

  const handleSignatureChange = (content: string) => {
    setSignature(content);
    // debouncing
    if (debounce) {
      clearTimeout(debounce);
    }
    debounce = setTimeout(() => {
      draftSettingSubmit(content);
    }, 1000);
  };

  const handleCalendlyChange = (content: string) => {
    setCalendly(content);
    // debouncing
    if (debounce) {
      clearTimeout(debounce);
    }
    debounce = setTimeout(() => {
      //draftSettingSubmit(content);
      updateCalendarLink({
        variables: {
          calendarLink: content,
        },
      }).then(() => {
        setShowToast(true);
        refetchCalendarSettings();
      });
    }, 1000);
  };

  const locked = isFree && !vipUser;

  const showCustomLabelSettings = inboxBehavior === E_EmailArchiveLevel.Custom;

  return (
    <PageLayout title="My assistant | Multiplayer">
      <div
        style={{
          position: "absolute",
          right: "0px",
          top: "0px",
        }}
      >
        <ToastMessage
          show={showToast}
          handleShow={setShowToast}
          success
          duration={3000}
        />
      </div>
      <div className={NotificationStyle}>
        <Text.Body2XS color="darkGrey">Email management</Text.Body2XS>
        <Text.Body2XS color="primary" marginTop={1}>
          Choose how your inbox is managed by default. If we detect that an
          email is urgent or important, it may remain in your inbox regardless
          of your selected setting.
        </Text.Body2XS>
        <Separator.Horizontal spacing={0} />
        <div
          style={{
            width: "100%",
          }}
        >
          <ServiceOptionsItem
            title="Auto-archive behavior:"
            subtitle={
              inboxManagementOptions.find((s) => s.value === inboxBehavior)
                ?.description
            }
            onSelect={(val) => handleSelectBehavior(val as E_EmailArchiveLevel)}
            Icons={[]}
            options={inboxManagementOptions}
            value={inboxBehavior}
          />
        </div>
      </div>
      <div className={NotificationStyle}>
        {showCustomLabelSettings && (
          <>
            <Separator.Horizontal spacing={2} />
            <Text.Body2XS color="primary" marginTop={1}>
              Note that if we detect that an email is important, we may bypass
              your settings and keep it in your inbox.
            </Text.Body2XS>
            <Separator.Horizontal spacing={0} />
            <div
              style={{
                width: "100%",
              }}
            >
              {labelSettingsData?.inboxLabelSettings?.map((setting) => (
                <ServiceOptionsItem
                  title={setting.name}
                  tag
                  onSelect={(val) =>
                    handleInboxManagementSelect(setting.name, val)
                  }
                  Icons={[]}
                  options={inboxSettingsOptions}
                  disabled={!showCustomLabelSettings}
                  value={
                    inboxManagement
                      .find((s) => s.name === setting.name)
                      ?.defaultBehavior?.toString() || setting.defaultBehavior
                  }
                />
              ))}
            </div>
          </>
        )}

        <Separator.Horizontal spacing={5} />
        <Text.Body2XS color="darkGrey">Meetings summaries</Text.Body2XS>
        <Text.Body2XS color="primary" marginTop={1}>
          Customize how Multiplayer joins your meetings to take notes and send
          personalized follow-up emails and summaries to each meeting attendee.
        </Text.Body2XS>
        <Separator.Horizontal spacing={0} />

        <ServiceOptionsItem
          title="Meetings to join:"
          subtitle="Select the type of meetings you want your assistant to join."
          onSelect={(val) => handleSelect("joinMeetings", val)}
          Icons={[]}
          options={meetingJoinOptions}
          disabled={allDisabled || locked}
          value={settings.joinMeetings?.toString()}
        />

        <ServiceOptionsItem
          title="After a meeting, create a draft follow up email for:"
          subtitle="A follow up email will be created for you after each meeting based on the notes taken."
          onSelect={(val) => handleSelect("followUpEmail", val)}
          Icons={[]}
          options={followupEmailOptions}
          disabled={allDisabled || locked}
          value={settings.followUpEmail?.toString()}
        />

        <ServiceOptionsItem
          title="Automatically send summaries via email to:"
          subtitle="An email with the meeting summary will be sent to the selected recipients."
          onSelect={(val) => handleSelect("meetingNoteEmail", val)}
          Icons={[]}
          options={meetingNoteEmailOptions}
          disabled={allDisabled || locked}
          value={settings.meetingNoteEmail?.toString()}
        />

        <Separator.Horizontal spacing={5} />
        <InputItem
          value={calendly}
          onChange={handleCalendlyChange}
          title="Calendar link"
          subtitle="Your assistant will use your scheduling link to suggest meeting times when drafting emails."
        />

        <Separator.Horizontal spacing={5} />
        <TextEditor
          value={signature}
          onChange={handleSignatureChange}
          title="Email Signature"
          subtitle="Customize the signature that will be added to your email draft replies. You can add formatted text, images, and links."
        />
      </div>
    </PageLayout>
  );
};
